import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ApiError, resendVerificationRequest } from '@/auth/api';
import { withNext } from '@/auth/nextPath';
import { useAuth } from '@/auth/useAuth';
import { PrimaryButton } from '@/components/PrimaryButton';
import { buyDeviceRequest } from '@/features/account/api';
import { goToCheckout } from '@/features/account/checkout';
import { purchaseConditions } from '@/features/account/purchases';
import { rememberPendingReservation } from '@/features/account/reservations';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';

/// `?buy=1` on the sheet: the buyer came back from signing in to buy this device.
const BUY_PARAM = 'buy';

type Problem = { kind: 'unavailable' } | { kind: 'paymentsOff' } | { kind: 'failed' } | null;

/**
 * The "Comprar" of a public device sheet (VEN-7), next to "Reservar" and gated the same way:
 *
 * - Without a session it takes the buyer to sign in, and back here with the dialog open.
 * - With the mail not verified it says so, with a way to get the link again (P-E1).
 * - With a verified mail it opens the confirmation with the conditions and the total, full
 *   screen on a phone (UC-057). Confirming holds the device thirty minutes and goes to pay in
 *   Mercado Pago.
 *
 * Staff sell from the panel, so for them the button is disabled.
 */
export function WebPurchaseAction({
  deviceId,
  deviceName,
  priceMinor,
  onUnavailable,
  onNeedsVerification,
}: {
  deviceId: string;
  deviceName: string;
  priceMinor: number | null;
  /// The device was taken while the dialog was open: the sheet reads it again on close.
  onUnavailable?: () => void;
  /// Next to "Reservar", the sheet shows one verification notice under both buttons, full width;
  /// without it, this shows its own.
  onNeedsVerification?: () => void;
}) {
  const { user, token, refreshUser } = useAuth();
  const { formatNullablePrice } = usePriceDisplay();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [problem, setProblem] = useState<Problem>(null);
  const autoOpened = useRef(false);
  const loginPath = withNext('/login', `/catalog/${deviceId}?${BUY_PARAM}=1`);
  const isStaff = Boolean(user && user.role !== 'CUSTOMER');

  const askToVerify = () => {
    rememberPendingReservation(deviceId);
    if (onNeedsVerification) onNeedsVerification();
    else setNeedsVerification(true);
  };

  const start = async () => {
    setProblem(null);

    if (!user || !token) {
      navigate(loginPath);
      return;
    }

    let verified = user.emailVerified;
    if (!verified) {
      // The mail may have been verified in another tab since this session started.
      setIsChecking(true);
      try {
        verified = Boolean((await refreshUser())?.emailVerified);
      } catch {
        verified = false;
      } finally {
        setIsChecking(false);
      }
    }

    if (!verified) {
      askToVerify();
      return;
    }

    setNeedsVerification(false);
    setOpen(true);
  };

  // Back from signing in with `?buy=1`: open the confirmation once, and drop the flag so a
  // reload does not open it again.
  useEffect(() => {
    if (autoOpened.current || searchParams.get(BUY_PARAM) !== '1' || !user) return;
    autoOpened.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete(BUY_PARAM);
    setSearchParams(next, { replace: true });
    if (user.role === 'CUSTOMER') void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  const resend = async () => {
    if (!user) return;
    setResendState('sending');
    try {
      await resendVerificationRequest(user.email);
      setResendState('sent');
    } catch {
      setResendState('failed');
    }
  };

  const close = () => {
    if (isSaving) return;
    setOpen(false);
    if (problem?.kind === 'unavailable') onUnavailable?.();
  };

  const confirm = async () => {
    if (!token) return;
    setIsSaving(true);
    setProblem(null);
    try {
      const purchase = await buyDeviceRequest(token, deviceId);
      // Stays "saving" while the browser leaves: a second click must not start anything else.
      goToCheckout(purchase.checkoutUrl);
    } catch (error) {
      setIsSaving(false);
      if (error instanceof ApiError && error.status === 409) {
        setProblem({ kind: 'unavailable' });
      } else if (error instanceof ApiError && error.status === 503) {
        setProblem({ kind: 'paymentsOff' });
      } else if (error instanceof ApiError && error.details?.code === 'EMAIL_NOT_VERIFIED') {
        setOpen(false);
        askToVerify();
      } else if (error instanceof ApiError && error.status === 401) {
        navigate(loginPath);
      } else {
        setProblem({ kind: 'failed' });
      }
    }
  };

  if (isStaff) {
    return (
      <Button variant="outlined" disabled fullWidth>
        Comprar
      </Button>
    );
  }

  const blocked = problem?.kind === 'unavailable' || problem?.kind === 'paymentsOff';

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        variant="outlined"
        fullWidth
        disabled={isChecking}
        onClick={() => void start()}
        sx={{ height: '100%' }}
      >
        {isChecking ? 'Verificando...' : 'Comprar'}
      </Button>
      {needsVerification ? (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Verificá tu email para comprar: te mandamos un enlace a {user?.email}. Cuando lo abras,
          volvés a este equipo.{' '}
          {resendState === 'sent' ? (
            <strong>Te enviamos un enlace nuevo.</strong>
          ) : (
            <Link
              component="button"
              type="button"
              onClick={() => void resend()}
              disabled={resendState === 'sending'}
              underline="always"
              sx={{ verticalAlign: 'baseline' }}
            >
              {resendState === 'sending' ? 'Enviando...' : 'Reenviar el enlace'}
            </Link>
          )}
          {resendState === 'failed'
            ? ' No pudimos enviarlo; volvé a intentar en un momento.'
            : null}
        </Alert>
      ) : null}

      <Dialog open={open} onClose={close} fullScreen={fullScreen} maxWidth="sm" fullWidth>
        <DialogTitle>Comprar {deviceName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {problem?.kind === 'unavailable' ? (
              <Alert severity="error">
                Este equipo ya no está disponible: alguien lo reservó o lo está comprando.{' '}
                <Link component={RouterLink} to="/catalog" underline="always">
                  Ver otros equipos
                </Link>
              </Alert>
            ) : null}
            {problem?.kind === 'paymentsOff' ? (
              <Alert severity="warning">
                La compra online no está disponible en este momento. Podés reservar el equipo y
                pagarlo en el local.
              </Alert>
            ) : null}
            {problem?.kind === 'failed' ? (
              <Alert severity="error">
                No pudimos conectar con Mercado Pago. Volvé a intentar.
              </Alert>
            ) : null}
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Total a pagar
              </Typography>
              <Typography variant="h5" component="p">
                {formatNullablePrice(priceMinor)}
              </Typography>
            </Stack>
            <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2.5 }}>
              {purchaseConditions.map((condition) => (
                <Typography component="li" key={condition}>
                  {condition}
                </Typography>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" disabled={isSaving} onClick={close}>
            {blocked ? 'Cerrar' : 'Cancelar'}
          </Button>
          {blocked ? null : (
            <PrimaryButton disabled={isSaving} onClick={() => void confirm()}>
              {isSaving ? 'Yendo a Mercado Pago...' : 'Pagar con Mercado Pago'}
            </PrimaryButton>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
